import { Save } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormSection } from '@components/form-section';
import { PageHeader } from '@components/page-header';
import { Spinner } from '@components/spinner';
import { StickyBar } from '@components/sticky-bar';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Api } from '@core/api';
import { links } from '@core/router';
import { injectTSDI } from '@core/tsdi';
import { ProductUpdateSupportDto } from '@api/WalletClient';

const EditProductForm = observer(() => {
    const { vendorId, productId } = useParams();
    const navigate = useNavigate();
    const { walletClient } = injectTSDI(Api);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [formData, setFormData] = useState<ProductUpdateSupportDto>({
        symbol: '',
        externalId: '',
        image: { objectKey: '', bucketName: '' }
    });

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                setLoading(true);
                const result =
                    await walletClient.supportApi.vendorSupportControllerGetProductDetails(
                        Number(vendorId),
                        Number(productId)
                    );
                const productData = result.data;
                if (productData) {
                    setFormData({
                        symbol: productData.symbol || '',
                        externalId: productData.externalId || '',
                        image: productData.image || {
                            objectKey: '',
                            bucketName: ''
                        }
                    });
                }
            } catch (err: any) {
                console.error('Error fetching product:', err);
                setError(
                    err.response?.data?.message ||
                        err.message ||
                        'Failed to fetch product'
                );
            } finally {
                setLoading(false);
            }
        };
        if (vendorId && productId) {
            fetchProduct();
        }
    }, [vendorId, productId]);

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);

            const dataToSend: ProductUpdateSupportDto = {
                ...formData,
                symbol:
                    formData.symbol?.trim() === '' ? undefined : formData.symbol
            };

            await walletClient.supportApi.vendorSupportControllerUpdateProduct(
                Number(vendorId),
                Number(productId),
                dataToSend
            );
            navigate(links.productDetails(Number(vendorId), Number(productId)));
        } catch (err: any) {
            console.error('Error updating product:', err);
            setError(
                err.response?.data?.message ||
                    err.message ||
                    'Failed to update product'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        navigate(links.productDetails(Number(vendorId), Number(productId)));
    };

    if (loading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (error && !formData.symbol) {
        return (
            <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <>
            <PageHeader
                title="Edit Product"
                description="Update product information"
            />

            <div className="flex flex-col gap-6 pb-24">
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                <FormSection title="Product Information">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div>
                            <TextField
                                label="Symbol"
                                type="text"
                                value={formData.symbol || ''}
                                onChange={(value) =>
                                    setFormData({
                                        ...formData,
                                        symbol: String(value)
                                    })
                                }
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                Product symbol or code
                            </p>
                        </div>

                        <div>
                            <TextField
                                label="External ID"
                                type="text"
                                value={formData.externalId || ''}
                                onChange={(value) =>
                                    setFormData({
                                        ...formData,
                                        externalId: String(value)
                                    })
                                }
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                ID from external source (if applicable)
                            </p>
                        </div>
                    </div>
                </FormSection>
            </div>

            <StickyBar>
                <Button
                    variant="ghost"
                    onClick={handleCancel}
                    disabled={saving}
                >
                    Cancel
                </Button>
                <Button onClick={handleSave} loading={saving}>
                    <Save />
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </StickyBar>
        </>
    );
});

export default EditProductForm;
